import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { operatingService } from '../../services/api';
import {
  Landmark,
  ArrowLeft,
  Plus,
  Download,
  Edit,
  DollarSign,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import NewOperatingTransactionModal from '../../components/operating/NewOperatingTransactionModal';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';

const OperatingAccountDetail = () => {
  const { accountId } = useParams();
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [account, setAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionFilters, setTransactionFilters] = useState({ limit: 50 });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updatingAccount, setUpdatingAccount] = useState(false);

  const fetchAccountDetails = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await operatingService.getAccount(accountId);
      setAccount(response.data);
    } catch (error) {
      console.error('Error fetching operating account details:', error);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const fetchTransactions = React.useCallback(async () => {
    try {
      const params = {};
      if (transactionFilters.limit) params.limit = transactionFilters.limit;

      const response = await operatingService.getAccountTransactions(accountId, params);
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    }
  }, [accountId, transactionFilters]);

  useEffect(() => {
    if (accountId) {
      fetchAccountDetails();
    }
  }, [accountId, fetchAccountDetails]);

  useEffect(() => {
    if (accountId && activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [accountId, activeTab, fetchTransactions]);

  const handleExport = () => {
    const csvContent = 'Operating Account Transaction Export\n\n' +
      'Date,Description,Client,Amount,Credit/Debit,Running Total\n' +
      transactions.map(tx =>
        `"${tx.transaction_date}","${tx.description || ''}","${tx.client_name || ''}",${tx.amount},"${tx.is_credit ? 'Credit' : 'Debit'}",${tx.running_total || ''}`
      ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operating-account-${account.account_number || account.name}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    showSuccess('Account data exported successfully!');
  };

  const handleEdit = () => {
    setEditFormData({
      name: account.name || '',
      status: account.status || 'active'
    });
    setShowEditModal(true);
  };

  const handleUpdateAccount = async () => {
    try {
      setUpdatingAccount(true);
      const response = await operatingService.updateAccount(accountId, editFormData);
      setAccount(response.data);
      setShowEditModal(false);
      showSuccess('Account updated successfully!');
    } catch (error) {
      console.error('Error updating account:', error);
      showError(error.response?.data?.detail || 'Failed to update account');
    } finally {
      setUpdatingAccount(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Operating Account Not Found</h3>
        <p className="mt-1 text-sm text-gray-500">The requested operating account could not be found.</p>
        <Link to="/operating-accounts" className="mt-4 inline-flex items-center text-blue-600 hover:text-gray-600">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Operating Accounts
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Landmark },
    { id: 'transactions', name: 'Transactions', icon: DollarSign },
    { id: 'reports', name: 'Reports', icon: TrendingUp }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Landmark className="mr-3 h-8 w-8 text-blue-600" />
              {account.name}
            </h1>
            <p className="text-gray-600 mt-1">
              {account.account_number ? `Account #${account.account_number}` : 'Operating Account'}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowTransactionModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New Transaction</span>
          </button>
          <button
            onClick={handleExport}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={handleEdit}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Edit className="h-4 w-4" />
            <span>Edit</span>
          </button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Current Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {operatingService.formatCurrency(account.current_balance)}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Status</p>
              <p className="mt-1">
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${operatingService.getAccountStatusColor(account.status)}`}>
                  {account.status}
                </span>
              </p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Landmark className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Opened</p>
              <p className="text-2xl font-bold text-gray-900">
                {account.opened_date ? operatingService.formatDate(account.opened_date) : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="mr-2 h-5 w-5" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Recent Transactions</h4>
                {account.recent_transactions && account.recent_transactions.length > 0 ? (
                  <div className="space-y-3">
                    {account.recent_transactions.slice(0, 5).map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {transaction.description || 'Transaction'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {operatingService.formatDate(transaction.transaction_date)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-medium ${
                            transaction.is_credit ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.is_credit ? '+' : ''}{operatingService.formatCurrency(transaction.amount)}
                          </p>
                          {transaction.running_total != null && (
                            <p className="text-xs text-gray-500">
                              Balance: {operatingService.formatCurrency(transaction.running_total)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No recent transactions</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex gap-4">
                <select
                  value={transactionFilters.limit}
                  onChange={(e) => setTransactionFilters({ ...transactionFilters, limit: parseInt(e.target.value) })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value={25}>25 transactions</option>
                  <option value={50}>50 transactions</option>
                  <option value={100}>100 transactions</option>
                  <option value={250}>250 transactions</option>
                </select>
              </div>

              {/* Transactions Table */}
              <div className="overflow-hidden">
                {transactions.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Running Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {operatingService.formatDate(transaction.transaction_date)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {transaction.description || '-'}
                            {transaction.is_refund && (
                              <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                                Refund
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.client_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <span className={parseFloat(transaction.amount) >= 0 ? 'text-green-600' : 'text-red-600'}>
                              {parseFloat(transaction.amount) >= 0 ? '+' : ''}{operatingService.formatCurrency(transaction.amount)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {transaction.running_total != null ? operatingService.formatCurrency(transaction.running_total) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.payment_method || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No transactions match your current filters.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="space-y-6">
              <h4 className="text-lg font-medium text-gray-900">Available Reports</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleExport}
                  className="p-4 border border-gray-300 rounded-lg text-left hover:bg-gray-50 transition-colors"
                >
                  <h5 className="font-medium text-gray-900">Transaction History</h5>
                  <p className="text-sm text-gray-500 mt-1">Export all transaction data as CSV</p>
                </button>
                <button
                  onClick={() => {
                    const content = `OPERATING ACCOUNT STATEMENT\n` +
                      `Date: ${new Date().toISOString().split('T')[0]}\n\n` +
                      `Account: ${account.name}\n` +
                      `Number: ${account.account_number || 'N/A'}\n` +
                      `Status: ${account.status}\n` +
                      `Balance: $${parseFloat(account.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
                    const blob = new Blob([content], { type: 'text/plain' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `operating-statement-${new Date().toISOString().split('T')[0]}.txt`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    showSuccess('Statement generated!');
                  }}
                  className="p-4 border border-gray-300 rounded-lg text-left hover:bg-gray-50 transition-colors"
                >
                  <h5 className="font-medium text-gray-900">Account Statement</h5>
                  <p className="text-sm text-gray-500 mt-1">Generate account summary statement</p>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Transaction Modal */}
      <NewOperatingTransactionModal
        isOpen={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        accountId={accountId}
        onTransactionCreated={() => {
          fetchAccountDetails();
          if (activeTab === 'transactions') {
            fetchTransactions();
          }
        }}
      />

      {/* Edit Account Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Operating Account</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Name *
                </label>
                <input
                  type="text"
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Account name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={editFormData.status || 'active'}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={updatingAccount}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateAccount}
                disabled={updatingAccount || !editFormData.name}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {updatingAccount ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <Edit className="mr-2 h-4 w-4" />
                    Update Account
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default OperatingAccountDetail;
